import React, { useState, useMemo, useCallback } from 'react';
import { ContractProperty, ContractPropertyTreeNode, TransformationConfig, TransformationType } from '../../types/contract';
import clsx from 'clsx';
import { ChevronUpIcon, ChevronDownIcon, Trash2, Type, Hash, CheckSquare, SquareStack, Box, CircleDot, Edit, ArrowRight, Shuffle, Shield, Sparkles, GitFork, Hand, PlusCircle } from 'lucide-react';
import { getTypeColorClass } from '../../utils/schemaUtils';
import ContractPropertyEditForm from './ContractPropertyEditForm';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import EventUsageBar from '../schema/EventUsageBar';
import { EventSchema } from '../../types/schema'; // Import EventSchema

interface ContractPropertyTableListProps {
  properties: ContractPropertyTreeNode[];
  isEditable: boolean;
  handleUpdateProperty: (propertyId: string, updates: Partial<ContractProperty>) => void;
  handleDeleteProperty: (propertyId: string) => void;
  handleAddProperty: (parentProperty: ContractPropertyTreeNode) => void;
  activeEditingPropertyId: string | null;
  setActiveEditingPropertyId: React.Dispatch<React.SetStateAction<string | null>>;
  availableMappings: { name: string; path: string; type: string; certaintyScore?: number }[];
  handleTransformationConfigUpdate: (propertyId: string, configUpdates: Partial<TransformationConfig>) => void;
  handleTransformationTypeChange: (propertyId: string, type: TransformationType) => void;
  allEvents: EventSchema[]; // New prop
  handlePropertyEventAssociationChange: (propertyId: string, eventId: string, isAssociated: boolean) => void; // New prop
}

type SortKey = 'path' | 'dataType' | 'isNormalized' | 'normalizedPath';
type SortDirection = 'asc' | 'desc'; // Corrected: Allow both 'asc' and 'desc'

const ContractPropertyTableList: React.FC<ContractPropertyTableListProps> = ({
  properties, // These properties now include normalized data
  isEditable,
  handleUpdateProperty, // Changed prop name
  handleDeleteProperty,
  handleAddProperty,
  activeEditingPropertyId,
  setActiveEditingPropertyId,
  availableMappings,
  handleTransformationConfigUpdate,
  handleTransformationTypeChange,
  allEvents, // Destructure allEvents
  handlePropertyEventAssociationChange, // Destructure handlePropertyEventAssociationChange
}) => {
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: SortDirection } | null>(null);
  const [hoveredPropertyId, setHoveredPropertyId] = useState<string | null>(null);
  const [unsavedChangesProperties, setUnsavedChangesProperties] = useState<Set<string>>(new Set());
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogConfirmText, setDialogConfirmText] = useState('Discard');
  const [dialogCancelText, setDialogCancelText] = useState('Keep Editing');
  const [dialogOnConfirm, setDialogOnConfirm] = useState<(() => void) | null>(null);

  const sortedProperties = useMemo(() => {
    let sortableItems = [...properties];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortConfig.key) {
          case 'path':
            aValue = a.path.toLowerCase();
            bValue = b.path.toLowerCase();
            break;
          case 'dataType':
            aValue = (a.dataType || '').toLowerCase();
            bValue = (b.dataType || '').toLowerCase();
            break;
          case 'isNormalized':
            aValue = a.isNormalized ? 1 : 0;
            bValue = b.isNormalized ? 1 : 0;
            break;
          case 'normalizedPath':
            aValue = (a.normalizedPath || '').toLowerCase();
            bValue = (b.normalizedPath || '').toLowerCase();
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [properties, sortConfig]);

  const requestSort = useCallback((key: SortKey) => {
    let direction: SortDirection = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  }, [sortConfig]);

  const getSortIcon = (key: SortKey) => {
    if (!sortConfig || sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon className="w-4 h-4 inline-block ml-1" />
    ) : (
      <ChevronDownIcon className="w-4 h-4 inline-block ml-1" />
    );
  };

  const truncatePath = (path: string, maxLength: number = 40) => {
    if (path.length <= maxLength) {
      return path;
    }
    const parts = path.split('.');
    if (parts.length === 1) {
      return path.substring(0, maxLength - 3) + '...';
    }
    const lastPart = parts[parts.length - 1];
    const firstPart = parts[0];
    const ellipsis = '...';
    const availableLength = maxLength - ellipsis.length - lastPart.length;

    if (availableLength <= firstPart.length + 3) {
      return firstPart.substring(0, availableLength - 3) + ellipsis + lastPart;
    }

    return firstPart + ellipsis + lastPart;
  };

  // Get the appropriate icon for the data type
  const getTypeIcon = useCallback((dataType?: string) => {
    switch (dataType) {
      case 'string':
        return <Type size={16} />;
      case 'number':
        return <Hash size={16} />;
      case 'boolean':
        return <CheckSquare size={16} />;
      case 'array':
        return <SquareStack size={16} />;
      case 'object':
        return <Box size={16} />;
      case 'null':
        return <CircleDot size={16} />;
      default:
        return <Type size={16} />;
    }
  }, []);

  const getTransformationDisplayName = useCallback((type?: TransformationType, config?: TransformationConfig) => {
    switch (type) {
      case 'mapping':
        if (config?.mappingType) {
          return `Mapping: ${config.mappingType.charAt(0).toUpperCase() + config.mappingType.slice(1)}`;
        }
        return 'Mapping';
      case 'enrichment':
        if (config?.enrichmentType) {
          return `Enrichment: ${config.enrichmentType.charAt(0).toUpperCase() + config.enrichmentType.slice(1)}`;
        }
        return 'Enrichment';
      case 'lua': return 'Lua Script';
      case 'spreading': return 'Spreading';
      default: return 'Transformation';
    }
  }, []);

  const handleExitEditing = useCallback(() => {
    // This function will now be called without arguments.
    // The logic to handle unsaved changes should be managed within the form itself
    // or by checking the unsaved changes state here.
    const propertyId = activeEditingPropertyId;
    if (propertyId && unsavedChangesProperties.has(propertyId)) {
      setDialogTitle('Discard Changes?');
      setDialogMessage('You have unsaved changes. Do you want to discard them?');
      setDialogConfirmText('Discard');
      setDialogOnConfirm(() => () => {
        setActiveEditingPropertyId(null);
        setUnsavedChangesProperties(prev => {
          const newSet = new Set(prev);
          if (propertyId) newSet.delete(propertyId);
          return newSet;
        });
        setShowDiscardConfirmation(false);
      });
      setDialogCancelText('Keep Editing');
      setShowDiscardConfirmation(true);
    } else {
      setActiveEditingPropertyId(null);
    }
  }, [activeEditingPropertyId, unsavedChangesProperties, setActiveEditingPropertyId]);

  const getMappingTypeDisplay = useCallback((property: ContractProperty) => {
    let text = 'N/A';
    let icon = null;
    let bgColor = 'bg-gray-100 dark:bg-gray-700';
    let textColor = 'text-gray-800 dark:text-gray-200';

    if (property.isNormalized) {
      // Check for "No match found" status first
      if (property.matchStatus === 'no-match') {
        text = 'No match found';
        icon = <CircleDot size={12} className="mr-1" />;
        bgColor = 'bg-purple-100 dark:bg-purple-900';
        textColor = 'text-purple-800 dark:text-purple-200';
      }
      // Check for manual mapping
      else if (property.matchType === 'manual') {
        text = 'Manual';
        icon = <Hand size={12} className="mr-1" />;
        bgColor = 'bg-gray-200 dark:bg-gray-600';
        textColor = 'text-gray-800 dark:text-gray-100';
      }
      // Check for AI matches (that aren't "no-match")
      else if (property.matchType === 'ai') {
        const certainty = property.aiCertaintyScore || 0;
        text = `AI (${(certainty * 100).toFixed(0)}%)`;
        icon = <Sparkles size={12} className="mr-1" />;
        if (certainty < 0.5) {
          bgColor = 'bg-red-100 dark:bg-red-900';
          textColor = 'text-red-800 dark:text-red-200';
        } else if (certainty < 0.8) {
          bgColor = 'bg-orange-100 dark:bg-orange-900';
          textColor = 'text-orange-800 dark:text-orange-200';
        } else {
          bgColor = 'bg-green-100 dark:bg-green-900';
          textColor = 'text-green-800 dark:text-green-200';
        }
      }
      // Check for auto-mapped properties (regex or direct mapping)
      else if (property.transformationConfig?.mappingType === 'direct' || property.transformationConfig?.mappingType === 'regex') {
        text = 'Auto-mapped';
        icon = <GitFork size={12} className="mr-1" />;
        bgColor = 'bg-blue-100 dark:bg-blue-900';
        textColor = 'text-blue-800 dark:text-blue-200';
      }
    }

    if (text === 'N/A') {
      return null;
    }

    return (
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${bgColor} ${textColor} whitespace-nowrap`}>
          {icon} {text}
        </span>
      </div>
    );
  }, []);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead>
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[25%] cursor-pointer"
              onClick={() => requestSort('path')}
            >
              Property Path {getSortIcon('path')}
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[15%] cursor-pointer"
            >
              Mapping Type
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[15%] cursor-pointer"
            >
              Transformation
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[20%] cursor-pointer"
              onClick={() => requestSort('normalizedPath')}
            >
              Schema Name {getSortIcon('normalizedPath')}
            </th>
            <th scope="col" className="relative px-6 py-3 w-[10%]">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {sortedProperties.map((property) => {
            const isEditingThisProperty = activeEditingPropertyId === property.id;
            const isHovered = hoveredPropertyId === property.id;
            const hasUnsavedChanges = unsavedChangesProperties.has(property.id);

            // property now already contains the merged normalized data
            return (
              <React.Fragment key={property.id}>
                <tr
                  className={clsx(
                    "hover:bg-blue-50 dark:hover:bg-gray-700 cursor-pointer",
                    isEditingThisProperty ? "bg-blue-50 dark:bg-blue-900/30" : "",
                    hasUnsavedChanges && !isEditingThisProperty ? "bg-yellow-50 dark:bg-yellow-900/30" : ""
                  )}
                  onClick={() => !isEditingThisProperty && isEditable && setActiveEditingPropertyId(property.id)}
                  onMouseEnter={() => setHoveredPropertyId(property.id)}
                  onMouseLeave={() => setHoveredPropertyId(null)}
                >
                  <td className="px-6 py-2 text-sm font-medium text-gray-900 dark:text-white"> 
                    <div className="flex items-center gap-2">
                      {/* Data Type Icon */}
                      {property.dataType && (
                        <span className={`inline-flex w-6 h-6 justify-center items-center rounded-full ${getTypeColorClass(property.dataType)}`}>
                          {getTypeIcon(property.dataType)}
                        </span>
                      )}
                      <div title={property.path}>
                        {truncatePath(property.path, 50)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-2 text-sm text-gray-500 dark:text-gray-300">
                    {getMappingTypeDisplay(property)}
                  </td>
                  <td className="px-6 py-2 text-sm text-gray-500 dark:text-gray-300">
                    {property.isNormalized && (
                      <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-white text-gray-800 dark:bg-gray-700 dark:text-gray-200 whitespace-nowrap">
                        <Shuffle size={12} className="mr-1" /> {getTransformationDisplayName(property.transformationType, property.transformationConfig)}
                      </span>
                      <ArrowRight size={16} />
                      </div>
                    )}
                    </td>
                    <td className="px-6 py-2 text-sm font-medium text-gray-900 dark:text-white">
                      <div className="flex items-center gap-2">
                        {property.dataType && (
                          <span className={`inline-flex w-6 h-6 justify-center items-center rounded-full ${getTypeColorClass(property.dataType)}`}>
                            {getTypeIcon(property.dataType)}
                          </span>
                        )}
                        <div title={property.normalizedPath}>
                          {truncatePath(property.normalizedPath || '', 50)}
                        </div>
                      </div>
                    </td>
                  <td className="px-6 py-2 text-sm text-gray-500 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      {property.isRequired && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                          Required
                        </span>
                      )}
                      {property.isPII && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900 dark:text-fuchsia-200">
                          <Shield size={12} className="mr-1" /> PII
                        </span>
                      )}
                      {property.eventUsageCount !== undefined && property.totalEventCount !== undefined && property.eventNames && (
                        <EventUsageBar usedCount={property.eventUsageCount} totalCount={property.totalEventCount} eventNames={property.eventNames} />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-2 text-right text-sm font-medium">
                    <div className={clsx(
                      "flex items-center justify-end space-x-2",
                      isHovered || isEditingThisProperty ? 'opacity-100' : 'opacity-0'
                    )}>
                      {isEditable && (
                        <>
                          {(property.dataType === 'object' || property.dataType === 'array') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddProperty(property);
                              }}
                              className="text-gray-600 hover:text-blue-600 hover:bg-blue-100 dark:text-gray-300 dark:hover:bg-blue-900/50 dark:hover:text-blue-400 p-1 rounded-md"
                              title="Add Sub-property"
                            >
                              <PlusCircle size={16} />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveEditingPropertyId(property.id);
                            }}
                            className="text-gray-600 hover:text-blue-600 hover:bg-blue-100 dark:text-gray-300 dark:hover:bg-blue-900/50 dark:hover:text-blue-400 p-1 rounded-md"
                            title="Edit Property"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProperty(property.id);
                            }}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                            title="Delete Property"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {isEditingThisProperty && (
                  <tr>
                    <td colSpan={6} className="p-0">
                      <ContractPropertyEditForm
                        property={property}
                        normalizedProperty={property}
                        availableMappings={availableMappings}
                        handleTransformationConfigUpdate={(propId, updates) => {
                          handleTransformationConfigUpdate(propId, updates);
                          setUnsavedChangesProperties(prev => new Set(prev).add(propId));
                        }}
                        handleTransformationTypeChange={(propId, type) => {
                          handleTransformationTypeChange(propId, type);
                          setUnsavedChangesProperties(prev => new Set(prev).add(propId));
                        }}
                        handlePropertyMappingUpdate={(propId, normalizedPath, sourceMatchType, aiCertaintyScore, aiSuggested) => {
                          handleUpdateProperty(propId, { normalizedPath, matchType: sourceMatchType, aiCertaintyScore, aiSuggested, isNormalized: true });
                          setUnsavedChangesProperties(prev => new Set(prev).add(propId));
                        }}
                        handleUpdateProperty={(propId, updates) => {
                          handleUpdateProperty(propId, updates);
                          setUnsavedChangesProperties(prev => new Set(prev).add(propId));
                        }}
                        onClose={handleExitEditing}
                        allEvents={allEvents} // Pass allEvents
                        handlePropertyEventAssociationChange={handlePropertyEventAssociationChange} // Pass handler
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {properties.length === 0 && (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">
          No properties match your current filters.
        </p>
      )}

      {/* Discard Changes Confirmation Dialog */}
      {showDiscardConfirmation && dialogOnConfirm && (
        <ConfirmDialog
          onClose={() => setShowDiscardConfirmation(false)}
          onConfirm={dialogOnConfirm}
          title={dialogTitle}
          message={dialogMessage}
          confirmText={dialogConfirmText}
          cancelText={dialogCancelText}
          isDanger={false}
        />
      )}
    </div>
  );
};

export default ContractPropertyTableList;
